/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef } from "react";
import { io } from "socket.io-client";

/**
 * Custom hook to manage Socket.IO connections and dynamic event listening
 *
 * @param {object} params
 * @param {number|string} params.userId - The unique identifier of the user
 * @param {string} params.userRole - The profile role ("admin", "seller", "driver")
 * @param {string} params.eventPrefix - The prefix of the event to listen to ("download", "coverage")
 * @param {function} params.onMessage - Callback function executed when a message is received
 */
export default function useSocket({ userId, userRole, eventPrefix, onMessage }) {
  const socketRef = useRef(null);
  const onMessageRef = useRef(onMessage);

  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  useEffect(() => {
    if (!userId || !userRole || !eventPrefix) return;

    const socket = io(process.env.REACT_APP_SOCKET_SERVER, {
      autoConnect: false,
      transports: ["websocket", "polling"],
    });

    socketRef.current = socket;
    socket.connect();

    socket.on("connect", () => {
      socket.emit("open", { id: userId, role: userRole });
    });

    const channel = `${eventPrefix}_${userRole}_${userId}`;
    const handleEventMessage = (payload) => {
      if (typeof onMessage === "function") {
        onMessage(payload);
      }
    };

    socket.on(channel, handleEventMessage);

    return () => {
      socket.emit("close", { id: userId, role: userRole });
      socket.off(channel, handleEventMessage);
      socket.disconnect();
    };
  }, [userId, userRole, eventPrefix]);

  return socketRef.current;
}
